// ============================================================
// MySQLSubqueriesAndViews.jsx
//
// Subqueries & Views (MySQL)
// Foundation · Module 05
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
} from "@mui/material";

/* Icons */
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LayersIcon from "@mui/icons-material/Layers";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SchemaIcon from "@mui/icons-material/Schema";
import FunctionsIcon from "@mui/icons-material/Functions";
import SpeedIcon from "@mui/icons-material/Speed";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold: "#DAA520",
    goldBright: "#FFD700",
    goldGlow: "rgba(218,165,32,0.15)",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "rgba(0,0,0,0.45)",
    codeText: "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
};

/* ───────────────── SUB COMPONENTS ───────────────── */

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading }}>
                {label}
            </Typography>
        </Box>
    );
}

function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 18px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                <Typography sx={{ fontWeight: 700, color: BRAND.white }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.7 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map(l => l.cmd).join("\n");

    const copy = () => {
        navigator.clipboard.writeText(fullText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 3 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map(c => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy"}>
                    <Box onClick={copy} sx={{ cursor: "pointer", color: BRAND.gold }}>
                        {copied ? <DoneIcon /> : <ContentCopyIcon />}
                    </Box>
                </Tooltip>
            </Box>

            <Box sx={{ p: 2 }}>
                {lines.map((l, i) => (
                    <Box key={i} sx={{ mb: 1 }}>
                        {l.comment && (
                            <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: BRAND.muted }}>
                                -- {l.comment}
                            </Typography>
                        )}
                        <Typography sx={{ fontFamily: "monospace", color: BRAND.codeText }}>
                            {l.cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 2 }}>
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    p: 2,
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    background: BRAND.surface,
                }}
            >
                <Box sx={{ display: "flex", gap: 1 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                    <Typography sx={{ color: BRAND.white }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>

            <Collapse in={open}>
                <Box sx={{ p: 2, background: "rgba(0,0,0,0.25)" }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

/* ───────────────── MAIN PAGE ───────────────── */

export default function MySQLSubqueriesAndViews() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 6 }}>
            <Container maxWidth="lg">

                {/* HERO */}
                <Chip
                    icon={<SchoolIcon sx={{ color: BRAND.gold }} />}
                    label="Foundation · Module 05"
                    sx={{ mb: 2, background: BRAND.goldGlow, color: BRAND.gold }}
                />

                <Typography variant="h3" sx={{ ...sectionHeading, mb: 2 }}>
                    Subqueries & <span style={{ color: BRAND.gold }}>Views</span>
                </Typography>

                <Typography sx={{ color: BRAND.muted, maxWidth: 780 }}>
                    Subqueries let SQL queries think in steps. Views let you save those
                    steps as reusable, virtual tables. Together, they unlock expressive,
                    readable, and maintainable database logic.
                </Typography>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* WHY */}
                <SectionTitle icon={<LightbulbIcon />} label="Why Subqueries & Views Matter" />
                <Typography sx={{ color: BRAND.muted, mb: 3 }}>
                    Complex questions are easier to answer when broken into smaller ones.
                    Subqueries allow you to nest logic. Views let you reuse that logic safely
                    without repeating SQL everywhere.
                </Typography>

                {/* KEY CONCEPTS */}
                <SectionTitle icon={<MenuBookIcon />} label="Core Concepts" />
                <Grid container spacing={2}>
                    {[
                        {
                            icon: <LayersIcon />,
                            title: "Subqueries",
                            description: "Queries inside other queries used to filter, compute, or compare data.",
                        },
                        {
                            icon: <FunctionsIcon />,
                            title: "Scalar vs Table Subqueries",
                            description: "Subqueries can return one value, one row, or many rows.",
                        },
                        {
                            icon: <SchemaIcon />,
                            title: "Correlated Subqueries",
                            description: "Subqueries that depend on the outer query’s current row.",
                        },
                        {
                            icon: <VisibilityIcon />,
                            title: "Views",
                            description: "Virtual tables created from SELECT queries to simplify reuse.",
                        },
                        {
                            icon: <SpeedIcon />,
                            title: "Performance Awareness",
                            description: "Some subqueries can be slower than JOINs if misused.",
                        },
                    ].map(c => (
                        <Grid item xs={12} sm={6} md={4} key={c.title}>
                            <ConceptCard {...c} />
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* PRACTICE */}
                <SectionTitle icon={<CodeIcon />} label="Hands‑On: Subqueries" />

                <CodeBlock
                    lines={[
                        {
                            comment: "Find students with IDs above the average",
                            cmd: `SELECT *
FROM students
WHERE id > (
  SELECT AVG(id) FROM students
);`,
                        },
                    ]}
                />

                <ExpandableExample title="Subquery with IN" icon={<LayersIcon />}>
                    <CodeBlock
                        lines={[
                            {
                                comment: "Students enrolled in any course",
                                cmd: `SELECT *
FROM students
WHERE id IN (
  SELECT student_id FROM enrollments
);`,
                            },
                        ]}
                    />
                </ExpandableExample>

                <ExpandableExample title="Correlated Subquery" icon={<SchemaIcon />}>
                    <CodeBlock
                        lines={[
                            {
                                comment: "Orders greater than a user's average order",
                                cmd: `SELECT *
FROM orders o
WHERE total > (
  SELECT AVG(total)
  FROM orders
  WHERE user_id = o.user_id
);`,
                            },
                        ]}
                    />
                </ExpandableExample>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* VIEWS */}
                <SectionTitle icon={<VisibilityIcon />} label="Hands‑On: Views" />

                <CodeBlock
                    lines={[
                        {
                            comment: "Create a reusable view",
                            cmd: `CREATE VIEW active_students AS
SELECT id, full_name, email
FROM students
WHERE is_active = TRUE;`,
                        },
                        {
                            comment: "Query the view like a table",
                            cmd: `SELECT * FROM active_students;`,
                        },
                    ]}
                />

                <ExpandableExample title="Updating through a View" icon={<VisibilityIcon />}>
                    <CodeBlock
                        lines={[
                            {
                                comment: "Simple views can be updatable",
                                cmd: `UPDATE active_students
SET email = 'new@mail.com'
WHERE id = 1;`,
                            },
                        ]}
                    />
                </ExpandableExample>

                <ExpandableExample title="Replacing or Dropping Views" icon={<SpeedIcon />}>
                    <CodeBlock
                        lines={[
                            { comment: "Modify a view", cmd: "CREATE OR REPLACE VIEW active_students AS SELECT id, full_name FROM students WHERE is_active = TRUE;" },
                            { comment: "Remove a view", cmd: "DROP VIEW active_students;" },
                        ]}
                    />
                </ExpandableExample>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* LEARNED */}
                <SectionTitle icon={<StarIcon />} label="What You've Learned" />
                <Grid container spacing={2}>
                    {[
                        "How subqueries break complex logic into steps",
                        "How IN and comparison subqueries work",
                        "How correlated subqueries differ from normal ones",
                        "What views are and why they’re useful",
                        "How to query and maintain views safely",
                        "When JOINs are better than subqueries",
                    ].map(i => (
                        <Grid item xs={12} sm={6} key={i}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <CheckCircleOutlineIcon sx={{ color: BRAND.gold }} />
                                <Typography sx={{ color: BRAND.muted }}>{i}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* FOOTER */}
                <Box sx={{ p: 3, background: BRAND.goldGlow, borderRadius: 2 }}>
                    <Typography sx={{ color: BRAND.white }}>
                        💡 <strong>Pro Tip:</strong> If a query is hard to read, turn part of it
                        into a view. Readability is a performance feature for humans.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
